import React, { useState, useRef, useEffect, useCallback } from 'react';
import './SearchableDropdown.css';

/* ── Option types ────────────────────────────────────────────── */

export interface DropdownOption {
  value: string;
  label: string;
  /** Depth for hierarchical indentation (0 = no indent) */
  depth?: number;
  /** Group header this option belongs to (rendered as a section label) */
  group?: string;
  /** Other words people type for this option (not shown): a search for "resta" finds
   *  "Quick Service / Fast Food (QSR)" when its keywords say "restaurant". */
  keywords?: string;
}

export interface SearchableDropdownProps {
  options: DropdownOption[];
  /** Selected value (single-select mode). Optional in multiSelect mode. */
  value?: string;
  /** Change handler (single-select mode). Optional in multiSelect mode. */
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  /** Extra class on the outer wrapper (so parent CSS can target width, etc.) */
  wrapperClassName?: string;
  /** If true, show error styling */
  hasError?: boolean;
  disabled?: boolean;
  /** Multi-select mode: clicking an option toggles it (dropdown stays open) and
   *  selected options are highlighted. Use with selectedValues + onToggleValue
   *  instead of value + onChange. */
  multiSelect?: boolean;
  /** Selected values in multiSelect mode (pass options pre-ordered if you want
   *  selected pinned to the top). */
  selectedValues?: string[];
  /** Toggle handler in multiSelect mode. */
  onToggleValue?: (value: string) => void;
  /** Optional pinned action row shown at the TOP of the open dropdown (e.g.
   *  "+ Add new folder"). It is NOT part of the options list and is not filtered
   *  by the search box — clicking it closes the dropdown and runs onClick. */
  actionRow?: { label: React.ReactNode; onClick: () => void };
}

/* ── Component ───────────────────────────────────────────────── */

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  options,
  value = '',
  onChange,
  placeholder = '-- Select --',
  className = '',
  wrapperClassName = '',
  hasError = false,
  disabled = false,
  multiSelect = false,
  selectedValues = [],
  onToggleValue,
  actionRow,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  /* ── Derived: selected label ────────────────────────────────── */
  const selectedOption = options.find(o => o.value === value);
  const displayLabel = selectedOption?.label ?? '';

  /* ── Multi-select derived state ─────────────────────────────── */
  const isOptionSelected = useCallback(
    (v: string) => multiSelect ? selectedValues.includes(v) : v === value,
    [multiSelect, selectedValues, value],
  );
  const multiCount = selectedValues.length;
  const multiSummary = multiCount === 0
    ? ''
    : multiCount === 1
      ? (options.find(o => o.value === selectedValues[0])?.label ?? '1 selected')
      : `${multiCount} selected`;
  // Closed-state text + whether a value exists (unifies single + multi).
  const hasValue = multiSelect ? multiCount > 0 : !!value;
  const closedText = multiSelect ? multiSummary : displayLabel;

  /* ── Derived: filtered options ──────────────────────────────── */
  // A typed search matches text anywhere in the label, the label's INITIALS ("SM" finds
  // "Sat Mehta" — the quick way to pick a person or business by keyboard), or the
  // option's other words (`keywords`: "resta" finds a fast-food type).
  const needle = search.trim().toLowerCase();
  const matched = needle
    ? options.filter(o => {
        const label = o.label.toLowerCase();
        if (label.includes(needle)) return true;
        // at the START of a keyword word only: "uber" must not find "youtuber"
        if (needle.length >= 2 && o.keywords
          && ` ${o.keywords.toLowerCase().replace(/[,\s]+/g, ' ')}`.includes(` ${needle}`)) return true;
        if (needle.length < 2 || /\s/.test(needle)) return false;
        const initials = label.split(/[^a-z0-9]+/).filter(Boolean).map(w => w[0]).join('');
        return initials.startsWith(needle);
      })
    : options;
  // In a FLAT list the matches that START with what was typed come first ("ele" lists
  // "Electricity" before "Telephone"; a leading account number is skipped, so "1000
  // Chequing" starts with "che" too), then those where a word or an other-word starts
  // with it, then the rest. Grouped and indented lists keep their own order.
  const flatList = !matched.some(o => o.group || (o.depth || 0) > 0);
  const startRank = (o: DropdownOption): number => {
    const label = o.label.toLowerCase();
    if (label.startsWith(needle) || label.replace(/^[\d.-]+\s+/, '').startsWith(needle)) return 0;
    const words = ` ${label} ${(o.keywords || '').toLowerCase()}`.replace(/[,\s]+/g, ' ');
    return words.includes(` ${needle}`) ? 1 : 2;
  };
  const filtered = needle && flatList
    ? matched.map((o, i) => ({ o, i, r: startRank(o) })).sort((x, y) => x.r - y.r || x.i - y.i).map(x => x.o)
    : matched;

  /* ── Close on outside click ─────────────────────────────────── */
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch('');
        setHighlightIdx(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /* ── Scroll highlighted item into view ──────────────────────── */
  useEffect(() => {
    if (highlightIdx >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('.sd-option');
      items[highlightIdx]?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIdx]);

  /* ── Select a value ─────────────────────────────────────────── */
  const selectValue = useCallback((val: string) => {
    onChange?.(val);
    setIsOpen(false);
    setSearch('');
    setHighlightIdx(-1);
  }, [onChange]);

  /* ── Open the dropdown ──────────────────────────────────────── */
  // `seed` pre-fills the search box so a keyboard user can Tab in and start
  // typing immediately (the first character opens + filters). Empty = plain open.
  const openWithSearch = useCallback((seed: string) => {
    if (disabled) return;
    setIsOpen(true);
    setSearch(seed);
    setHighlightIdx(seed ? 0 : -1);
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [disabled]);
  const open = useCallback(() => openWithSearch(''), [openWithSearch]);

  /* ── Close, optionally keeping keyboard focus on the field ──── */
  const close = useCallback((keepFocus: boolean) => {
    setIsOpen(false);
    setSearch('');
    setHighlightIdx(-1);
    if (keepFocus) {
      // The search input unmounts on close; put focus back on the closed field so
      // Enter "accepts and stays", and the next Tab moves on from here.
      setTimeout(() => (wrapperRef.current?.querySelector('.sd-display') as HTMLElement | null)?.focus(), 0);
    }
  }, []);

  /* ── Commit the highlighted match of a TYPED search ─────────── */
  // Multi-select ADDS it (never un-ticks an owner that is already chosen).
  const commitTyped = useCallback((): boolean => {
    if (!needle || highlightIdx < 0 || highlightIdx >= filtered.length) return false;
    const val = filtered[highlightIdx].value;
    if (multiSelect) { if (!selectedValues.includes(val)) onToggleValue?.(val); }
    else onChange?.(val);
    return true;
  }, [needle, highlightIdx, filtered, multiSelect, selectedValues, onToggleValue, onChange]);

  /* ── Keyboard navigation ────────────────────────────────────── */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx(prev => (prev < filtered.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx(prev => (prev > 0 ? prev - 1 : filtered.length - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // Enter = accept and STAY on the field.
      if (needle) {
        // Typed a search ("SM", "Sat"): take the highlighted match and close. More
        // owners/folders are added by opening the list again and picking them.
        if (commitTyped()) close(true);
      } else if (highlightIdx >= 0 && highlightIdx < filtered.length) {
        if (multiSelect) {
          onToggleValue?.(filtered[highlightIdx].value);   // browsing the list: toggle, keep open
        } else {
          selectValue(filtered[highlightIdx].value);
          close(true);
        }
      }
    } else if (e.key === 'Tab') {
      // Tab = accept the typed match AND move on. The close is deferred so the browser
      // moves focus from the search input first (it unmounts when the list closes).
      commitTyped();
      setTimeout(() => close(false), 0);
    } else if (e.key === 'Escape') {
      close(true);
    }
  };

  /* ── Build grouped rendering ────────────────────────────────── */
  const renderOptions = () => {
    if (filtered.length === 0) {
      return <div className="sd-no-results">No matches found</div>;
    }

    const hasGroups = filtered.some(o => o.group);

    if (hasGroups) {
      // Render grouped options
      const groups: { label: string; items: { opt: DropdownOption; globalIdx: number }[] }[] = [];
      const ungrouped: { opt: DropdownOption; globalIdx: number }[] = [];
      let globalIdx = 0;

      filtered.forEach(opt => {
        if (opt.group) {
          let grp = groups.find(g => g.label === opt.group);
          if (!grp) {
            grp = { label: opt.group!, items: [] };
            groups.push(grp);
          }
          grp.items.push({ opt, globalIdx });
        } else {
          ungrouped.push({ opt, globalIdx });
        }
        globalIdx++;
      });

      return (
        <>
          {ungrouped.map(({ opt, globalIdx: gi }) => renderSingleOption(opt, gi))}
          {groups.map(grp => (
            <React.Fragment key={grp.label}>
              <div className="sd-group-label">{grp.label}</div>
              {grp.items.map(({ opt, globalIdx: gi }) => renderSingleOption(opt, gi))}
            </React.Fragment>
          ))}
        </>
      );
    }

    // No groups — flat list
    return filtered.map((opt, idx) => renderSingleOption(opt, idx));
  };

  const renderSingleOption = (opt: DropdownOption, idx: number) => {
    const isHighlighted = idx === highlightIdx;
    const isSelected = isOptionSelected(opt.value);
    const indent = opt.depth ? opt.depth * 16 : 0;

    return (
      <div
        key={opt.value}
        className={`sd-option ${multiSelect ? 'sd-option-multi' : ''} ${isHighlighted ? 'sd-option-highlighted' : ''} ${isSelected ? 'sd-option-selected' : ''}`}
        style={indent ? { paddingLeft: 12 + indent } : undefined}
        onMouseEnter={() => setHighlightIdx(idx)}
        onMouseDown={(e) => {
          e.preventDefault(); // keep focus on input
          if (multiSelect) {
            onToggleValue?.(opt.value);                 // toggle; dropdown stays open
            setTimeout(() => inputRef.current?.focus(), 0);
          } else {
            selectValue(opt.value);
          }
        }}
      >
        {multiSelect ? (
          <>
            <span className="sd-option-label">{opt.label}</span>
            {isSelected && <span className="sd-option-check">✓</span>}
          </>
        ) : opt.label}
      </div>
    );
  };

  /* ── Render ─────────────────────────────────────────────────── */
  return (
    <div
      className={`sd-wrapper ${wrapperClassName} ${hasError ? 'sd-error' : ''} ${disabled ? 'sd-disabled' : ''}`}
      ref={wrapperRef}
    >
      {/* Closed state: looks like a select */}
      {!isOpen ? (
        <div
          className={`sd-display ${className}`}
          onClick={open}
          tabIndex={disabled ? -1 : 0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
              e.preventDefault();
              open();
            } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
              // Type-to-search: any printable character opens the dropdown and seeds
              // the filter with it, so a keyboard user who Tabs in can just start typing.
              e.preventDefault();
              openWithSearch(e.key);
            }
          }}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          <span className={hasValue ? 'sd-display-text' : 'sd-display-placeholder'}>
            {hasValue ? closedText : placeholder}
          </span>
          <span className="sd-chevron">▾</span>
        </div>
      ) : (
        /* Open state: text input with dropdown */
        <>
          <div className={`sd-input-row ${className}`}>
            <input
              ref={inputRef}
              autoFocus   /* focus as it mounts, so the 2nd typed character is never dropped */
              className="sd-search-input"
              type="text"
              value={search}
              onChange={e => { setSearch(e.target.value); setHighlightIdx(0); }}
              onKeyDown={handleKeyDown}
              placeholder={hasValue ? closedText : placeholder}
              autoComplete="off"
            />
            {/* Clear / close button (single-select only) */}
            {!multiSelect && value && (
              <button
                className="sd-clear-btn"
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectValue('');
                }}
                title="Clear selection"
                type="button"
              >
                ✕
              </button>
            )}
            <span className="sd-chevron sd-chevron-open">▴</span>
          </div>
          {/* tabIndex -1: a scrollable list is a Tab stop in current browsers; Tab from the
              search box must land on the NEXT FIELD, not on the list that is about to close. */}
          <div className="sd-dropdown" ref={listRef} tabIndex={-1}>
            {actionRow && (
              <div
                className="sd-action-row"
                onMouseDown={(e) => {
                  e.preventDefault();          // keep focus off; don't toggle an option
                  setIsOpen(false);
                  setSearch('');
                  setHighlightIdx(-1);
                  actionRow.onClick();
                }}
              >
                {actionRow.label}
              </div>
            )}
            {renderOptions()}
          </div>
        </>
      )}
    </div>
  );
};

export default SearchableDropdown;
